import { computed, ref, Ref } from "vue";

import POSConfigModule from "@/store/modules/pos-config";
import DeviceModule from "@/store/modules/device";
import UserModule from "@/store/modules/user";
import {
  getTokenScanHistory,
  redeemByBarCode,
  redeemOrderTokens,
  unscanOrderToken,
} from "@/api/check-in/check-in";
import {
  IRedeemByBarCodeResult,
  IRedeemResult,
  ITicketScanHistory,
} from "@/types/check-in";
import { LogService } from "@/services/logging";

/**
 * The engine checks every ticket of a request in parallel. Chunks keep one
 * large group from holding the shared access control too long.
 */
const CHUNK_SIZE = 50;

/**
 * Get the text of a failed request. The engine explains a bad POS
 * configuration, for example a configuration with no area.
 *
 * @param {unknown} error the error of the request.
 * @returns {string} the text of the engine, or an empty string.
 */
function engineMessage(error: unknown): string {
  const failure = error as {
    response?: { data?: { error?: { message?: string } } };
  };

  return failure?.response?.data?.error?.message || "";
}

export interface ICheckInActionApi {
  isRunning: Ref<boolean>;
  results: Ref<IRedeemResult[]>;
  errorMessage: Ref<string>;
  checkIn: (orderId: string, tokenIds: string[]) => Promise<IRedeemResult[]>;
  checkInByBarCode: (barCode: string) => Promise<IRedeemByBarCodeResult | null>;
  undo: (orderId: string, tokenId: string) => Promise<boolean>;
  readTokenHistory: (
    orderId: string,
    tokenId: string
  ) => Promise<ITicketScanHistory>;
}

/**
 * Send tickets to the engine and keep the results.
 *
 * @returns {ICheckInActionApi} the state, the two check-in functions, the
 * undo and the scans of one ticket.
 */
export function useCheckInAction(): ICheckInActionApi {
  const isRunning = ref(false);
  const results = ref<IRedeemResult[]>([]);
  const errorMessage = ref("");

  // Sales context. The engine rejects a group that the operator cannot use.
  const groupId = computed(() => POSConfigModule.groupId);
  const posConfigId = computed(() => POSConfigModule.selectedConfig?.id);

  function context() {
    return {
      groupId: Number(groupId.value),
      posConfigId: Number(posConfigId.value),
      // 0 means the terminal has no linked device.
      deviceId: DeviceModule.deviceId || undefined,
      userId: UserModule.userId ?? undefined,
    };
  }

  /**
   * Check in tickets of one order. A chunk that fails stops the run, but
   * the results of the chunks before it stay valid.
   *
   * @param {string} orderId the order of the tickets.
   * @param {string[]} tokenIds the tickets to check in.
   * @returns {Promise<IRedeemResult[]>} the results the engine returned.
   */
  async function checkIn(
    orderId: string,
    tokenIds: string[]
  ): Promise<IRedeemResult[]> {
    isRunning.value = true;
    errorMessage.value = "";
    results.value = [];

    try {
      for (let i = 0; i < tokenIds.length; i += CHUNK_SIZE) {
        const chunk = tokenIds.slice(i, i + CHUNK_SIZE);
        const chunkResults = await redeemOrderTokens(orderId, {
          ...context(),
          tokenIds: chunk,
        });

        results.value = [...results.value, ...chunkResults];
      }
    } catch (error) {
      errorMessage.value = engineMessage(error);
      LogService.info("Check-in: the engine refused the request", {
        error,
        params: { orderId, sent: tokenIds.length, done: results.value.length },
      });
    } finally {
      isRunning.value = false;
    }

    return results.value;
  }

  /**
   * Check in one ticket from a scanned code.
   *
   * @param {string} barCode the code that the scanner read.
   * @returns {Promise<IRedeemByBarCodeResult | null>} the result, or null
   * if the request failed.
   */
  async function checkInByBarCode(
    barCode: string
  ): Promise<IRedeemByBarCodeResult | null> {
    isRunning.value = true;
    errorMessage.value = "";

    try {
      return await redeemByBarCode({ ...context(), barCode });
    } catch (error) {
      errorMessage.value = engineMessage(error);
      LogService.info("Check-in: the scan request failed", { error });
      return null;
    } finally {
      isRunning.value = false;
    }
  }

  /**
   * Take back the entry of one ticket. The caller handles a failure.
   *
   * @param {string} orderId the order of the ticket.
   * @param {string} tokenId the ticket to take back.
   * @returns {Promise<boolean>} true when the engine took the entry back.
   */
  async function undo(orderId: string, tokenId: string): Promise<boolean> {
    return unscanOrderToken(orderId, tokenId, context().groupId);
  }

  /**
   * Get the scans of one ticket. The caller handles a failure.
   *
   * @param {string} orderId the order of the ticket.
   * @param {string} tokenId the ticket to read.
   * @returns {Promise<ITicketScanHistory>} the scans of the ticket.
   */
  async function readTokenHistory(
    orderId: string,
    tokenId: string
  ): Promise<ITicketScanHistory> {
    return getTokenScanHistory(orderId, tokenId, context().groupId);
  }

  return {
    isRunning,
    results,
    errorMessage,
    checkIn,
    checkInByBarCode,
    undo,
    readTokenHistory,
  };
}
